/**
 * ReviewsSection - Main component displaying customer reviews overview
 * Contains all sub-components: MainReviewCard, RegionalReviewCard, StarRating
 */

import { MainReviewCard } from "./MainReviewCard";
import { RegionalReviewCard } from "./regional-review-card";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { ImageField } from "@prismicio/client";
import { calculateCombinedRating } from "~/lib/etrusted/calculations";
import type { ETrustedAggregateRating } from "~/lib/etrusted/client.server";

// ============================================
// Types
// ============================================

export interface ETrustedReview {
  id: string;
  rating: number;
  title: string;
  comment: string;
  createdAt: string;
  submittedAt: string;
  state: string;
  type: string;
  verificationType: string;
  customer: {
    firstName: string;
    lastName: string;
    fullName: string;
  };
  transaction?: {
    reference: string;
    date: string;
  };
  [key: string]: any;
}

export interface Channel {
  id: string;
  name: string;
  locale: string;
  url: string;
  countryCode?: string; // NL, DE, FR, BE
  [key: string]: any;
}

export interface ReviewsSectionProps {
  // Channel data
  channel?: Channel; // Current/matched channel
  channels: Channel[]; // All available channels

  // Reviews data
  reviews?: {
    totalElements: number;
    items: ETrustedReview[];
    paging?: any;
  };

  // Scores - using eTrusted types for consistency
  scores?: ETrustedAggregateRating; // Current channel scores
  allChannelScores?: Record<string, ETrustedAggregateRating>; // All channels scores

  // Brand
  brandImage?: ImageField; // Prismic brand image field
  logo?: ImageField; // Prismic logo image field
}


export function ReviewsSection({
  channel,
  channels,
  reviews: _reviews,
  scores,
  allChannelScores = {},
  brandImage,
  logo,
}: ReviewsSectionProps) {
   const { $pt } = usePrismicLabels();
  // Note: _reviews is available for future use (individual review cards)
  void _reviews;
  // Calculate combined rating across all channels using centralized function
  const combinedRating = calculateCombinedRating(allChannelScores);
  
  // Fallback to current channel scores if no combined data
  const combinedYearRating = combinedRating.rating || scores?.["365days"]?.rating || 0;
  const totalAllChannelReviews = combinedRating.reviewCount || scores?.["365days"]?.count || 0;
  const yearRating = scores?.["365days"];


  return (
    <section className="reviews_section_container">
      <div className="container pt-5 pb-2">
        {/* Header */}
        <div className="mb-5">
          <h1 className="sans-serif strong mb-3 reviews_section_title">
            {$pt("reviews_page_title", {totalReviews: String(totalAllChannelReviews)})}
          </h1>
          <p className="text-gray reviews_section_description">
            {$pt("reviews_page_description")}
          </p>
        </div>

        {/* Main Cards Section */}
        <div className="p-3 p-md-4 pl-0 mb-5 bg-gray-lighter-4">
          <div className="row align-items-stretch">
            {/* Main Card (left on desktop, top on mobile) */}
            <div className="col-12 col-lg-5 col-xl-4 mb-4 mb-lg-0 pl-0">
              <MainReviewCard
                rating={combinedYearRating || yearRating?.rating || 0}
                totalReviews={totalAllChannelReviews}
                brandImage={brandImage}
                logo={logo}
              />
            </div>

            {/* Regional Cards (right on desktop, bottom on mobile) */}
            <div className="col-12 col-lg-7 col-xl-8 reviews_section_regional_cards_column">
              <div className="row align-content-center reviews_section_regional_cards_row">
                {[...channels].sort((a, b) => {
                  if (a.id === channel?.id) return -1;
                  if (b.id === channel?.id) return 1;
                  return 0;
                }).map((ch) => {
                  const channelRating =
                    allChannelScores[ch.id] ||
                    (ch.id === channel?.id ? scores : null);

                  return (
                    <div key={ch.id} className="col-12 col-sm-6 mb-3 reviews_section_regional_cards_item">
                      <RegionalReviewCard
                        name={ch.name}
                        rating={channelRating?.["365days"]?.rating || 0}
                        reviewCount={channelRating?.["365days"]?.count || 0}
                        locale={ch.locale}
                        countryCode={ch.countryCode}
                        channelId={ch.id}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ReviewsSection;
